import { createContext, useContext, useState, useCallback, useEffect, type ReactNode } from 'react';

export type Theme = 'dark' | 'light';
const ORDER: Theme[] = ['dark', 'light'];

interface ThemeContextValue {
  theme: Theme;
  lightMode: boolean;
  toggleTheme: () => void; // cycles dark ↔ light
}

const ThemeContext = createContext<ThemeContextValue>({
  theme: 'dark',
  lightMode: false,
  toggleTheme: () => undefined,
});

function stored(): Theme {
  try {
    const t = localStorage.getItem('theme');
    return t === 'light' ? t : 'dark';
  } catch { return 'dark'; }
}

export function ThemeProvider({ children }: { children: ReactNode }) {
  const [theme, setTheme] = useState<Theme>(stored);

  useEffect(() => {
    document.body.classList.toggle('light-mode', theme === 'light');
    try { localStorage.setItem('theme', theme); } catch { /* blocked storage */ }
  }, [theme]);

  const toggleTheme = useCallback(() => {
    const root = document.documentElement;
    root.classList.add('theme-fade');
    window.setTimeout(() => root.classList.remove('theme-fade'), 260);
    setTheme(t => ORDER[(ORDER.indexOf(t) + 1) % ORDER.length]);
  }, []);

  return (
    <ThemeContext.Provider value={{ theme, lightMode: theme === 'light', toggleTheme }}>
      {children}
    </ThemeContext.Provider>
  );
}

export function useTheme(): ThemeContextValue {
  return useContext(ThemeContext);
}
